import type {
  ResponseContractError,
  ResponseRequestContext,
} from '../contracts/response-contract-error'

export const RESPONSE_CONTRACT_MESSAGE = 'Your action may have succeeded, but something went wrong while processing the response. Refresh the page and check whether it worked.'

export type ResponseRecoverySnapshot = Readonly<{
  banner?: Readonly<{
    message: string
    focus: boolean
  }>
  flashFailures: readonly string[]
  flashFocus?: boolean
}>

type FormErrorTarget = (messages: string[]) => void
type Subscriber = (snapshot: ResponseRecoverySnapshot) => void

export function createResponseRecovery() {
  const formTargets = new Map<string, FormErrorTarget>()
  const subscribers = new Set<Subscriber>()
  let state: ResponseRecoverySnapshot = { flashFailures: [] }

  const publish = () => subscribers.forEach((subscriber) => subscriber(state))
  const setState = (next: ResponseRecoverySnapshot) => {
    state = next
    publish()
  }

  return {
    snapshot: () => state,
    subscribe(subscriber: Subscriber) {
      subscribers.add(subscriber)
      subscriber(state)
      return () => subscribers.delete(subscriber)
    },
    registerFormTarget(requestId: string, target: FormErrorTarget) {
      formTargets.set(requestId, target)
      return () => {
        if (formTargets.get(requestId) === target) formTargets.delete(requestId)
      }
    },
    recover(_error: ResponseContractError, context: ResponseRequestContext) {
      if (context.semantics === 'form-request') {
        const target = formTargets.get(context.id)
        if (target) {
          target([RESPONSE_CONTRACT_MESSAGE])
          return
        }
      }

      setState({
        ...state,
        banner: {
          message: RESPONSE_CONTRACT_MESSAGE,
          focus: context.action === 'explicit',
        },
      })
    },
    recordFlashFailures(failures: readonly string[], focus = false) {
      setState({ ...state, flashFailures: [...new Set(failures)], flashFocus: focus })
    },
    dismissBanner() {
      const { banner: _banner, ...rest } = state
      setState(rest)
    },
    clearAfterValidResponse() {
      setState({ flashFailures: [] })
    },
  }
}

export const responseRecovery = createResponseRecovery()
